import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import moment from "moment/moment";
import Switch from "react-bootstrap-switch";
import {Card, CardBody, CardTitle, Col, Collapse, FormGroup, Input, Label, Modal, Row} from "reactstrap";
import {useSelector} from "react-redux";
import * as Yup from "yup";

// components
import ActivityLangs from "./ActivityLangs";
import Separator from "../../../../components/Separator";
import SelectDates from "../../../../components/dates/SelectDates";
import SelectGeneric from "../../../../components/select/selectGeneric";
import {getCurrentDateForCodeField} from "../../../../util/DateTools";
import {GenericButton} from "../../../../components/buttons/customButtons";
import {LogHistoryModal} from "../../../../components/logHistory/logHistoryModal";

// labels
import {TT_CLOSE, TT_DELETE, TT_SAVE} from "../../../../components/i18n/toolTips";
import {
    FL_ADITIONAL_COST, FL_AUDIENCE_TYPE, FL_CATEGORY,
    FL_DATE, FL_DOES_IT_REPEAT,
    FL_END_HOUR, FL_FORUM, FL_HIGHLIGHT, FL_HOST,
    FL_START_HOUR, FL_SUB_CATEGORY
} from "../../../../components/i18n/formLabels";
import {FORM, LB_ACTIVE, REQUIRED} from "../../../../components/i18n/i18n";

// actions
import {genericIndexedActions} from "../../../../redux/actions/generics/GenericIndexedActions";

// services
import {itineraryService} from "../../../../jwt/_services/eventx/ItineraryService";

// providers
import {placeProvider} from "../../../../providers/placeProvider";
import {categoryProvider} from "../../../../providers/categoryProvider";
import {typeAudienceProvider} from "../../../../providers/eventx/typeAudienceProvider";

// selectors
import {getStateByPath} from "../../../../redux/selectors";
import {activityProvider} from "../../../../providers/activityProvider";



export const ActivityItinerary = forwardRef((props, ref) => {
    const path = props.path || 'event/itinerary';
    const [udn] = useState(props.udn || null);
    const [app] = useState(props.app || null);
    const [parent] = useState(props.parent || null);

    const [domainObject, setDomainObject] = useState(null);
    const [status, setStatus] = useState( domainObject?.status !== undefined ? domainObject?.status : true);
    const [hourStart, setHourStart] = useState(  '' )
    const [hourEnd, setHourEnd] = useState(  '' )
    const [date, setDate] = useState(  '' )
    const [placeId, setPlaceId] = useState(  domainObject?.place?.id || null);
    const [modal, setModal] = useState(false);
    const [categoryId, setCategoryId] = useState(  domainObject?.category?.id || null);
    const [subCategoryId, setSubCategoryId] = useState(  domainObject?.subCategory?.id || null);

    // activity data
    const [activity, setActivity] = useState(  domainObject?.activity || null);
    const [typeAudienceId, setTypeAudienceId] = useState( domainObject?.typeAudience?.id || null);
    const [additionalCost, setAdditionalCost] = useState(domainObject?.aditionalCost !== undefined ? domainObject?.aditionalCost : false);
    const [highlight, setHighlight] = useState(domainObject?.highlight !== undefined ? domainObject?.highlight : false);
    const [host, setHost] = useState(domainObject?.pax || []);
    const [langs, setLangs] = useState(domainObject?.langs || []);
    const translatableLangs = domainObject?.translatedLangs || [];
    //-----------------------------------------

    const modalRef = useRef(null);


    // repeat parameters
    const pathDates = props.path+"/dates";
    const componentState = useSelector((state) => getStateByPath(state, pathDates));
    const [repeat, setRepeat] = useState(false);
    const [dates, setDates] = useState([]);
    const [datesToRepeat, setDatesToRepeat] = useState([]);
    const [validationsError, setValidationsError] = useState([]);

    useEffect(() => {
        if(parent?.dates == null || !date)
            return

        dateToRepeat(parent?.dates)
    }, [componentState, date, repeat]);


    // get activity data
    useEffect(() => {
        if(!activity || app == null)
            return

        getActivity(activity)
    }, [activity, app]);


    // get activity data
    const getActivity = (activity) => {
        activityProvider.show(activity, app?.fbDataBase?.id)
            .then(response => {
                setTypeAudienceId(response?.typeAudience?.id || null);
                setAdditionalCost(response?.aditionalCost != null ? response?.aditionalCost : false);
                setHighlight(response?.highlight != null ? response?.highlight : false);
                setLangs(response?.langs || []);
            })
            .catch(error => console.error(error))
    }

    const dateToRepeat = (dates) => {
        const filterDates = dates.filter(item => {
            if(item?.date !== date)
                return item
        }).map(item => item?.date)

        setDates(filterDates)
    }


    const validationSchema = Yup.object().shape({
        categoryId: Yup.mixed().required('Category is required'),
        typeAudienceId: Yup.mixed().required('Type audience is required'),
        placeId: Yup.mixed().required('Foro is required')
    });

    const validateForm = async submit  => {
        const nullKeys = [];

        try {
            await validationSchema.validate({ categoryId, typeAudienceId, placeId });
        } catch (error) {
            for (const key in error.value) {
                if (!error.value[key]) {
                    nullKeys.push(key);
                }
            }
        }

        if(submit && nullKeys.length === 0)
            onSubmit()

        setValidationsError(nullKeys);
    };

    const onSubmit = () => {
        let code = domainObject?.activity?.code || `${parent?.code}_activity_` + getCurrentDateForCodeField().toLowerCase()

        let dataRequest = {
            fbDataBase :  { id : app?.fbDataBase?.id },
            item : {
                status,
                date,
                datesToRepeat,
                pax:host,
                timeStart: hourStart,
                timeEnd: hourEnd,
                event: { id : parent?.id },
                activity: {
                    code,
                    status: true,
                    udn: {id: udn?.id},
                    aditionalCost: additionalCost,
                    pax:host,
                    highlight,
                    langs
                }
            }
        };

        if (activity != null)
            dataRequest.item.activity.id = activity;

        if(typeAudienceId != null)
            dataRequest.item.activity.typeAudience = { id: typeAudienceId };

        if (categoryId != null) {
            dataRequest.item.category = {id: categoryId};
            dataRequest.item.activity.category = {id: categoryId};
        }

        if (subCategoryId != null)
            dataRequest.item.subCategory = { id : subCategoryId };

        if (placeId != null)
            dataRequest.item.place = { id : placeId };

        if(domainObject?.id !== undefined) {
            dataRequest.item.id = domainObject?.id

            genericIndexedActions.updateAll(`${path}/itinerary`, itineraryService, dataRequest)
        } else {
            genericIndexedActions.saveAll(`${path}/itinerary`, itineraryService, dataRequest)
        }

        closeModal();
    };

    const onDelete = (id) => {
        const db = app?.fbDataBase?.id
        genericIndexedActions.delete(`${path}/itinerary`, itineraryService, id, db)
        closeModal();
    }

    const closeModal = () => {
        setDomainObject(null);
        setDate('');
        setHourStart('');
        setHourEnd('');
        setTypeAudienceId(null);
        setAdditionalCost(null);
        setHighlight(null);
        setLangs([]);
        setHost(null);
        setStatus(null);
        setCategoryId(null);
        setSubCategoryId(null);
        setActivity(null);
        setPlaceId(null);
        setValidationsError([])
        setModal(!modal);
    }

    const showModal = () => setModal(!modal);

    useImperativeHandle(ref, () => ({
        showModal(_item, _start, _end) {
            if (_item?.id !== undefined) {
                setDomainObject(_item);
                setDate(_item?.date || '')
                setHourStart(_item?.timeStart || '');
                setHourEnd(_item?.timeEnd || '');
            } else {
                const formated = moment(_start).format("YYYY-MM-DD")
                setDomainObject(null);
                setDate(formated || '');
                setHourStart(moment(_start).format("HH:mm") || '');
                setHourEnd(moment(_end).format("HH:mm")  || '');
                setLangs( []);
            }

            setActivity(_item?.activity || null);
            setHost(_item?.pax?.id || null);
            setStatus(_item?.status!=null ? _item.status : true);
            setCategoryId(_item?.category?.id || null);
            setSubCategoryId(_item?.subCategory?.id || null);
            setPlaceId(_item?.place?.id || null);
            setValidationsError([])
            setModal(!modal);
        }
    }));

    return <Modal isOpen={modal} toggle={showModal} modalClassName="menu-modal-form">
        <Card>
            <CardTitle className="bg-light border-bottom p-3 mb-0 modal-title">
                <div className={'title-modal'}>
                    <i className="far fa-edit mr-2"/>{'Actividad y Programación'}
                </div>
                <div className="top-right">
                    <GenericButton
                        style={'table'}
                        icon={'fa-save'}
                        toolTip={TT_SAVE}
                        permission={'add'}
                        onClick={()=> validateForm(true)}
                    />
                    {
                        domainObject?.id !== undefined && <GenericButton
                            style={'danger-secondary'}
                            toolTip={TT_DELETE}
                            icon={'fa-trash'}
                            permission={'delete'}
                            onClick={()=> onDelete(domainObject?.id)}
                        />
                    }
                    <Separator/>
                    <GenericButton
                        style={'danger-secondary'}
                        toolTip={TT_CLOSE}
                        icon={'fa-times'}
                        permission={'search'}
                        onClick={_ => setModal(false)}
                    />
                </div>
            </CardTitle>
            <CardBody className={'card-body-modal'}>
                <Row>
                    <Col md="12">
                        <FormGroup>
                            <Label>{FL_CATEGORY}</Label>
                            <SelectGeneric
                                initialValue={ categoryId }
                                setExternalValue={ (val) => {
                                    setCategoryId(val)

                                    if(val && validationsError.length > 0)
                                        setValidationsError(validationsError.filter(item => item !== 'categoryId'))
                                } }
                                provider = { categoryProvider }
                                customFilter={{
                                    onlyRegistered:true,
                                    fbDataBase : { id :app?.fbDataBase?.id },
                                }}
                            />
                            { validationsError.includes('categoryId') && <div className="invalid-feedback" style={{'display':'block'}}>{REQUIRED}</div>}
                        </FormGroup>
                    </Col>
                    <Col md="6">
                        <FormGroup>
                            <Label>{ FL_FORUM }</Label>
                            <SelectGeneric
                                initialValue={ placeId }
                                setExternalValue={ val => {
                                    setPlaceId(val)

                                    if(val && validationsError.length > 0)
                                        setValidationsError(validationsError.filter(item => item !== 'placeId'))
                                } }
                                provider = { placeProvider }
                                customFilter={{
                                    onlyRegistered: true,
                                    fbDataBase :  { id : app?.fbDataBase?.id },
                                    item: { udn: {id: udn?.id } }
                                }}
                                resetValue={ false }
                            />
                            { validationsError.includes('placeId') && <div className="invalid-feedback" style={{'display':'block'}}>{REQUIRED}</div>}
                        </FormGroup>
                    </Col>
                    <Col md="6">
                        <FormGroup>
                            <Label>{ FL_AUDIENCE_TYPE }</Label>
                            <SelectGeneric
                                initialValue={ typeAudienceId }
                                setExternalValue={(val)=> {
                                    setTypeAudienceId(val)

                                    if(val && validationsError.length > 0)
                                        setValidationsError(validationsError.filter(item => item !== 'typeAudienceId'))
                                }}
                                provider = { typeAudienceProvider }
                            />
                            { validationsError.includes('typeAudienceId') && <div className="invalid-feedback" style={{'display':'block'}}>{REQUIRED}</div>}
                        </FormGroup>
                    </Col>

                </Row>

                <Row>
                    <Col>
                        <FormGroup>
                            <Label>{ FL_ADITIONAL_COST }</Label>
                            <br/>
                            <Switch
                                animate={true}
                                offText={"No"}
                                onText={"Si"}
                                value={additionalCost}
                                onChange={_ => setAdditionalCost(!additionalCost)}
                            />
                        </FormGroup>
                    </Col>
                    <Col>
                        <FormGroup>
                            <Label>{FL_HIGHLIGHT}</Label>
                            <br/>
                            <Switch
                                animate={true}
                                offText={"No"}
                                onText={"Si"}
                                value={highlight}
                                onChange={_ => setHighlight(!highlight)}
                            />
                        </FormGroup>
                    </Col>
                    <Col>
                        <FormGroup>
                            <Label>{LB_ACTIVE}</Label>
                            <br/>
                            <Switch
                                animate={true}
                                offText={"No"}
                                onText={"Si"}
                                value={ status }
                                onChange={ _ => setStatus(!status) }
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <FormGroup>
                            <Label>{ FL_DATE }</Label>
                            <Input type="date"
                                   name="date"
                                   value={ date }
                                   onChange={ e => setDate( e.target.value ) }
                                   placeholder={ 'Fecha' }
                            />
                        </FormGroup>
                    </Col>
                    <Col>
                        <FormGroup>
                            <Label>{FL_START_HOUR}</Label>
                            <Input
                                type="time"
                                name="timeStart"
                                value={ hourStart }
                                onChange={ e => setHourStart( e.target.value ) }
                                placeholder="Ingrese hora"
                            />
                        </FormGroup>
                    </Col>
                    <Col>
                        <FormGroup>
                            <Label>{FL_END_HOUR}</Label>
                            <Input
                                type="time"
                                name="timeEnd"
                                value={ hourEnd }
                                onChange={ e => setHourEnd( e.target.value ) }
                                placeholder="Ingrese hora"
                            />
                        </FormGroup>
                    </Col>
                    {/*<Col>*/}
                    {/*    <FormGroup>*/}
                    {/*        <Label>{FL_DOES_IT_REPEAT}</Label>*/}
                    {/*        <br/>*/}
                    {/*        <Switch*/}
                    {/*            animate={true}*/}
                    {/*            offText={"No"}*/}
                    {/*            onText={"Si"}*/}
                    {/*            value={repeat}*/}
                    {/*            onChange={_ => setRepeat(!repeat)}*/}
                    {/*        />*/}
                    {/*    </FormGroup>*/}
                    {/*</Col>*/}
                </Row>

                {/*<Collapse isOpen={repeat} >*/}
                {/*    <Row>*/}
                {/*        <Col>*/}
                {/*            <SelectDates*/}
                {/*                dates={dates}*/}
                {/*                onChange={setDatesToRepeat}*/}
                {/*            />*/}
                {/*        </Col>*/}
                {/*    </Row>*/}
                {/*</Collapse>*/}

                <Row>
                    {/* TODO: Se comentan estos campos temporalmente para limpiar la vista, cuando sean necesarios se vuelven a activar. */}
                    {/*<Col>*/}
                    {/*    <Label>{ FL_HOST }</Label>*/}
                    {/*    <SelectGeneric*/}
                    {/*        initialValue={host}*/}
                    {/*        setExternalValue={setHost}*/}
                    {/*        provider={paxProvider}*/}
                    {/*    />*/}
                    {/*</Col>*/}

                </Row>

                <Row>
                    <Col>
                        <ActivityLangs
                            langs={langs}
                            setLangs={setLangs}
                            appCode={app.code}
                            translatableLangs={translatableLangs}
                        />
                    </Col>
                </Row>
            </CardBody>
        </Card>

        {
            showModal && <LogHistoryModal
                ref={ modalRef }
                item={ domainObject }
            />
        }
    </Modal>
})
